<template>
  <v-app id="app" class="app">
    <div
      v-if="_showLogo"
      class="d-flex justify-center align-center fill-width pt-10"
    >
      <v-img :src="require('@/assets/logo.png')" max-width="20rem" />
    </div>

    <v-main>
      <router-view />
    </v-main>

    <AlertBar ref="alertBar" />

    <VersionBadge />
  </v-app>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { eventBus } from "@/eventBus";
import { getAttendance } from "@/services/attendance";
import AlertBar from "./components/alertBar/AlertBar.vue";
import VersionBadge from "./components/versionBadge/VersionBadge.vue";
import { showError } from "./utils";

export default {
  name: "App",

  components: {
    AlertBar,
    VersionBadge,
  },

  data() {
    return {
      interval: null,
    };
  },

  created() {
    this.handleContinueAttendance();
    window.addEventListener("beforeunload", this.handleClearInterval);
  },

  beforeDestroy() {
    eventBus.$off("appointments:created", this.onAppointmentCreated);
    eventBus.$off("appointments:started", this.onAppointmentStarted);
    eventBus.$off("appointments:transferred", this.onAppointmentTransferred);
  },

  beforeMount() {
    this.$root.$on("alert", this.handleAlert);
    this.$root.$on("attendance:finish", this.handleAttendanceFinish);
    eventBus.$on("appointments:created", this.onAppointmentCreated);
    eventBus.$on("appointments:started", this.onAppointmentStarted);
    eventBus.$on("appointments:transferred", this.onAppointmentTransferred);
  },

  mounted() {
    this.handlePingSession();
  },

  computed: {
    ...mapState("socket", [
      "attendance",
      "inAttendance",
      "inQueue",
      "code",
      "isConnected",
      "appointment",
    ]),

    ...mapGetters({ _preSelection: "preSelection" }),

    _showLogo() {
      const routes = ["Attendance", "ChatBot", "ChatCx"];

      return (
        this.$vuetify.breakpoint.mdAndUp || !routes.includes(this.$route.name)
      );
    },
  },

  watch: {
    inAttendance(value) {
      if (value) {
        this.$router
          .push({ path: `/attendance/${this.attendance._id}` })
          .catch(() => {});
      }
    },

    inQueue(value) {
      if (value) {
        const isAppointmentQueue =
          this.appointment && this.appointment.autoScheduled === true;

        if (isAppointmentQueue && !this.appointment._id) {
          console.warn("Appointment ID not available yet");
          return;
        }

        const queuePath = isAppointmentQueue
          ? `/queue-appointments/${this.appointment._id}`
          : "/queue";

        const isAlreadyOnQueuePage =
          this.$route.path.startsWith("/queue-appointments/") ||
          this.$route.path === "/queue";

        if (!isAlreadyOnQueuePage && this.$route.path !== queuePath) {
          console.log("Redirecting to queue:", queuePath);
          this.$router.push({ path: queuePath }).catch(() => {});
        }
      }
    },
  },

  methods: {
    ...mapActions("socket", [
      "setAttendance",
      "requestAttendance",
      "setInQueue",
      "cancelAttendance",
      "finishAttendance",
      "disconnectSocket",
      "setCode",
      "pingEmit",
      "setMessages",
      "addMessage",
      "setAppointment",
      "updateAppointment",
    ]),

    async handleContinueAttendance() {
      const queryID = this.$route.query.attendanceID;

      if (this.attendance?._id || queryID) {
        await this.getAttendance(queryID);

        if (this.attendance?.status === "requested") {
          this.handleRequestAttendance();
        } else {
          if (this.attendance?.status === "pending")
            return this.$router
              .push(
                Object.keys(this._preSelection).length &&
                  this._preSelection?.chatType
                  ? `/${this._preSelection.chatType}`
                  : "/chat-cx"
              )
              .catch(() => {});

          if (this.attendance?.status === "waiting") {
            const isAppointmentQueue =
              this.appointment && this.appointment.autoScheduled === true;

            if (isAppointmentQueue && !this.appointment._id) {
              console.warn(
                "Appointment ID not available in handleContinueAttendance"
              );
              return;
            }

            const queuePath = isAppointmentQueue
              ? `/queue-appointments/${this.appointment._id}`
              : "/queue";
            return this.$router.push(queuePath).catch(() => {});
          }

          if (this.attendance?.status === "in-progress") {
            return this.$router
              .push(`/attendance/${this.attendance._id}`)
              .catch(() => {});
          }

          this.handleDestroy();
        }
      }
    },

    async getAttendance(id) {
      try {
        const { data } = await getAttendance(id || this.attendance?._id, {
          code: this.code,
        });

        this.setAttendance(data);

        /* Comentado pois estava resetando as mensagens que já estão persistent na store
        this.setMessages([]);

        data.messages.forEach((el) => {
          this.addMessage(el);
        }); */
      } catch (error) {
        console.log(error);
        // this.setAttendance(null);
      }
    },

    async handleRequestAttendance() {
      await this.requestAttendance({
        data: {
          protocol: this.attendance.protocol,
          deskID: this.attendance.desk._id,
        },
        callback: (error, data) => {
          console.log("requestAttendance:data", data);
          if (error) {
            console.log("requestAttendance:error", error);
            throw new Error(
              "Falha ao tentar solicitar atendimento. Tente novamente"
            );
          }
          // Update Attendance on Store
          this.setAttendance(data);
          this.setInQueue(true);
          this.setMessages([]);
        },
      });
    },

    handleDestroy() {
      if (this.attendance) {
        this.handleAttendanceFinish();
      }

      this.handleClearInterval();

      this.$root.$off("alert", this.handleAlert);
      this.$root.$off("attendance:finish", this.handleAttendanceFinish);
    },

    handleAttendanceFinish() {
      try {
        const attendanceID = this.attendance?._id;
        if (!attendanceID) {
          return;
        }

        const payload = {
          attendanceID,
        };

        this.setMessages([]);

        if (["pending", "waiting"].includes(this.attendance.status)) {
          return this.cancelAttendance({
            data: payload,
            callback: (err, data) => {
              console.log("cancelAttendance:data", data);

              if (err) {
                console.log("cancelAttendance:error", err);
                throw new Error(err);
              }

              this.handleAlert({
                message: "Atendimento cancelado!",
                type: "warning",
              });
            },
          });
        }

        if (["in-progress"].includes(this.attendance.status)) {
          return this.finishAttendance({
            data: payload,
            callback: (err, data) => {
              console.log("finishAttendance:data", data);

              this.setCode(data.code);

              if (err) {
                console.log("finishAttendance:error", err);
                throw new Error(err);
              }

              this.handleAlert({
                message: "Atendimento finalizado!",
                type: "info",
              });
            },
          });
        }
      } catch (error) {
        this.showError(error);
      } finally {
        this.setAttendance(null);
      }
    },

    handlePingSession() {
      if (this.interval) this.handleClearInterval();

      this.pingEmit();

      this.interval = setInterval(() => {
        if (this.isConnected) return;

        console.log("ping");

        this.pingEmit();
      }, 10000);
    },

    handleAlert(event) {
      this.$refs.alertBar.handleAlert(event);
    },

    handleClearInterval() {
      clearInterval(this.interval);
    },

    onAppointmentCreated(data) {
      const appointment = data.appointment || data;

      if (appointment?.autoScheduled === true) {
        this.finishAttendanceSilently();

        this.setAppointment(appointment);
        this.setInQueue(true);
      }
    },

    finishAttendanceSilently() {
      try {
        const attendanceID = this.attendance?._id;
        if (!attendanceID) {
          return;
        }

        const payload = {
          attendanceID,
        };

        this.setMessages([]);

        if (["pending", "waiting"].includes(this.attendance.status)) {
          this.cancelAttendance({
            data: payload,
            callback: (err) => {
              if (err) {
                console.log(
                  "finishAttendanceSilently - cancelAttendance:error",
                  err
                );
                return;
              }
              this.setAttendance(null);
            },
          });
          return;
        }

        if (["in-progress"].includes(this.attendance.status)) {
          this.finishAttendance({
            data: payload,
            callback: (err, data) => {
              if (err) {
                console.log(
                  "finishAttendanceSilently - finishAttendance:error",
                  err
                );
                return;
              }

              if (data?.code) {
                this.setCode(data.code);
              }

              this.setAttendance(null);
            },
          });
          return;
        }
      } catch (error) {
        console.log("finishAttendanceSilently:error", error);
      }
    },

    onAppointmentStarted(data) {
      console.log("App.vue onAppointmentStarted:", data);
      const appointment = data.appointment || data;

      if (this.appointment?._id && appointment?._id === this.appointment._id) {
        console.log("Updating appointment with meetingLink");
        this.updateAppointment({
          isUserTurn: true,
          showMeetLink: true,
          meetingLink: appointment.meetingLink,
        });
      } else {
        console.log("Appointment ID mismatch or not set", {
          currentAppointmentId: this.appointment?._id,
          receivedAppointmentId: appointment?._id,
        });
      }
    },
    async onAppointmentTransferred(data) {
      const appointment = data.appointment || data;

      console.log(`Appointment Transferred`, appointment);

      if (appointment?._id) {
        await this.setAppointment(appointment);

        this.$nextTick(() => {
          this.$router
            .push({ path: `/queue-appointments/${appointment._id}` })
            .catch(() => {});
        });
      }
    },

    showError,
  },
};
</script>

<style lang="scss" scoped>
.app {
  background: rgba(17, 17, 17, 0.089) !important;
}
</style>
